// GradientFilter.ts
import * as PIXI from 'pixi.js';
import gsap from 'gsap';

const hexTo01 = (hex: number): [number, number, number] => [
  ((hex >> 16) & 0xff) / 255,
  ((hex >> 8) & 0xff) / 255,
  (hex & 0xff) / 255,
];

export interface GradientFilterOpts {
  angle?: number; // radians (0 = left→right)
  colorA?: [number, number, number]; // 0..1
  colorB?: [number, number, number]; // 0..1
  opacity?: number; // 0..1
}

export class GradientFilter extends PIXI.Filter {
  constructor(opts?: GradientFilterOpts) {
    const vert = `
      precision mediump float;
      attribute vec2 aVertexPosition;
      attribute vec2 aTextureCoord;
      uniform mat3 projectionMatrix;
      varying vec2 vTexCoord;
      void main() {
        vTexCoord = aTextureCoord;
        gl_Position = vec4((projectionMatrix * vec3(aVertexPosition, 1.0)).xy, 0.0, 1.0);
      }
    `;

    const frag = `
      precision mediump float;
      varying vec2 vTexCoord;

      uniform vec3 uColorA;
      uniform vec3 uColorB;
      uniform float uAngle;   // radians
      uniform float uOpacity; // 0..1

      float project(vec2 uv, float angle) {
        vec2 dir = vec2(cos(angle), sin(angle));
        vec2 p = uv - 0.5;
        float t = dot(p, dir) + 0.5;
        return clamp(t, 0.0, 1.0);
      }

      void main() {
        float t = project(vTexCoord, uAngle);
        vec3 col = mix(uColorA, uColorB, t);
        gl_FragColor = vec4(col, uOpacity);
      }
    `;

    super(vert, frag, {
      uColorA: new Float32Array(opts?.colorA ?? [1, 0.4, 0.4]),
      uColorB: new Float32Array(opts?.colorB ?? [1, 0.85, 0.3]),
      uAngle: opts?.angle ?? 0,
      uOpacity: opts?.opacity ?? 1,
    });
  }

  // Convenience accessors
  get colorA(): [number, number, number] {
    const a = this.uniforms.uColorA as Float32Array;
    return [a[0], a[1], a[2]];
  }
  set colorA(rgb01: [number, number, number]) {
    (this.uniforms.uColorA as Float32Array).set(rgb01);
  }

  get colorB(): [number, number, number] {
    const b = this.uniforms.uColorB as Float32Array;
    return [b[0], b[1], b[2]];
  }
  set colorB(rgb01: [number, number, number]) {
    (this.uniforms.uColorB as Float32Array).set(rgb01);
  }

  get angle(): number {
    return this.uniforms.uAngle as number;
  }
  set angle(rad: number) {
    this.uniforms.uAngle = rad;
  }

  get opacity(): number {
    return this.uniforms.uOpacity as number;
  }
  set opacity(v: number) {
    this.uniforms.uOpacity = v;
  }

  // --- Animation helpers (all return gsap.core.Timeline) ---

  /**
   * Tween both gradient colors to new targets (hex or rgb01).
   */
  animateToColors(options: {
    toA: number | [number, number, number];
    toB: number | [number, number, number];
    duration?: number;
    ease?: string;
    delay?: number;
    onStart?: () => void;
  }): gsap.core.Timeline {
    const toA = Array.isArray(options.toA) ? options.toA : hexTo01(options.toA);
    const toB = Array.isArray(options.toB) ? options.toB : hexTo01(options.toB);
    const duration = options.duration ?? 1.2;
    const ease = options.ease ?? 'power1.inOut';
    const delay = options.delay ?? 0;

    // Proxy arrays (GSAP won't tween Float32Array reliably)
    const proxy = {
      a: [...this.colorA],
      b: [...this.colorB],
    };

    const tl = gsap.timeline({ paused: true, delay, onStart: options.onStart });
    tl.to(
      proxy.a,
      {
        duration,
        ease,
        0: toA[0],
        1: toA[1],
        2: toA[2],
        onUpdate: () => {
          this.colorA = proxy.a as [number, number, number];
        },
      },
      0,
    ).to(
      proxy.b,
      {
        duration,
        ease,
        0: toB[0],
        1: toB[1],
        2: toB[2],
        onUpdate: () => {
          this.colorB = proxy.b as [number, number, number];
        },
      },
      0,
    );

    return tl;
  }

  /**
   * Cycle through multiple color pairs (palettes). Great for looping backgrounds.
   */
  animatePaletteCycle(options: {
    palettes: Array<[number, number]>; // [hexA, hexB]
    perStep?: number; // seconds per hop
    ease?: string;
    yoyo?: boolean;
    repeat?: number; // -1 for infinite
    delay?: number;
  }): gsap.core.Timeline {
    const {
      palettes,
      perStep = 1.5,
      ease = 'power1.inOut',
      yoyo = true,
      repeat = -1,
      delay = 0,
    } = options;
    if (!palettes || palettes.length < 2) {
      return this.animateToColors({
        toA: palettes?.[0]?.[0] ?? 0xffffff,
        toB: palettes?.[0]?.[1] ?? 0xffffff,
        duration: perStep,
        ease,
        delay,
      });
    }

    const tl = gsap.timeline({ paused: true, repeat, yoyo, delay });
    // Start at current; hop through palettes
    for (let i = 1; i < palettes.length; i++) {
      const [aHex, bHex] = palettes[i];
      tl.add(
        this.animateToColors({
          toA: aHex,
          toB: bHex,
          duration: perStep,
          ease,
        }),
      );
    }
    return tl;
  }

  /**
   * Rotate the gradient direction.
   */
  animateAngle(options: {
    to?: number; // radians
    by?: number; // delta radians (adds to current)
    duration?: number;
    ease?: string;
    repeat?: number;
    yoyo?: boolean;
    delay?: number;
  }): gsap.core.Timeline {
    const {
      to,
      by,
      duration = 2,
      ease = 'power1.inOut',
      repeat = 0,
      yoyo = false,
      delay = 0,
    } = options;
    const target = to !== undefined ? to : this.angle + (by ?? 0);
    const tl = gsap.timeline({ delay });
    tl.to(this, { duration, ease, angle: target, repeat, yoyo });
    return tl;
  }

  /**
   * Pulse overall opacity of the gradient.
   */
  animateOpacity(options: {
    to?: number; // 0..1
    from?: number; // optional
    duration?: number;
    ease?: string;
    repeat?: number; // -1 for infinite
    yoyo?: boolean;
    delay?: number;
  }): gsap.core.Timeline {
    const {
      to = 1,
      from,
      duration = 1,
      ease = 'power1.inOut',
      repeat = 0,
      yoyo = false,
      delay = 0,
    } = options;
    if (from !== undefined) this.opacity = from;
    const tl = gsap.timeline({ delay });
    tl.to(this, { duration, ease, opacity: to, repeat, yoyo });
    return tl;
  }

  /**
   * Convenience: build a single TL that combines angle + palette cycling.
   */
  animateCombo(options: {
    palettes: Array<[number, number]>;
    perStep?: number;
    angleBy?: number; // radians added per palette step
    anglePerStepDur?: number; // seconds
    ease?: string;
    repeat?: number; // -1 to loop forever
    yoyo?: boolean;
    delay?: number;
  }): gsap.core.Timeline {
    const {
      palettes,
      perStep = 1.5,
      angleBy = Math.PI / 6,
      anglePerStepDur = perStep,
      ease = 'power1.inOut',
      repeat = -1,
      yoyo = true,
      delay = 0,
    } = options;

    const master = gsap.timeline({ paused: true, repeat, yoyo, delay });

    for (let i = 1; i < palettes.length; i++) {
      const [a, b] = palettes[i];
      const step = gsap.timeline();
      step.add(
        this.animateToColors({ toA: a, toB: b, duration: perStep, ease }),
        0,
      );
      step.add(
        this.animateAngle({ by: angleBy, duration: anglePerStepDur, ease }),
        0,
      );
      master.add(step);
    }
    return master;
  }
}
